import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Tooltip,
  Button,
  Paper,
  Chip,
  useMediaQuery,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableChartIcon from "@mui/icons-material/TableChart";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, Link } from "react-router-dom";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import LightbulbOutlineIcon from "@mui/icons-material/LightbulbOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { BackButton, beautifyGradeText } from "./functions";
import { AnimatedStep } from "./Animations";
import ThemeFlowBox from "./ThemeFlowBox";
import { useTheme } from "@mui/material/styles";
import {
  collection,
  query,
  where,
  getDocs,
  or,
  and,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import RubricsDialog from "./RubricsDialog";

import GradeSelector from "./GradeSelector";
import GradeSelectorPopover from "./GradeSelectorPopover";

const RubricsDrawer = ({
  open,
  onClose,
  user,
  selectedRubric,
  onRubricSelect,
  grade,
  setGrade,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(1); // Start with user's own matrices
  const [isLoadingRubrics, setIsLoadingRubrics] = useState(true);
  const [userRubrics, setUserRubrics] = useState([]);
  const [standardRubrics, setStandardRubrics] = useState([]);
  const [isRubricsDialogOpen, setIsRubricsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [viewingRubric, setViewingRubric] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Add these new state variables to your existing state declarations
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserRubric, setSelectedUserRubric] = useState(null);
  const matrixRefs = useRef({});

  const [latestRubric, setLatestRubric] = useState(null);

  // Add this useEffect to load the latest rubric from localStorage when component mounts
  useEffect(() => {
    const savedLatestRubric = localStorage.getItem("latestSelectedRubric2");
    if (savedLatestRubric) {
      try {
        setLatestRubric(JSON.parse(savedLatestRubric));
      } catch (error) {
        console.error("Error parsing latest rubric from localStorage:", error);
      }
    }
  }, []);

  // Add this helper function to save the rubric selection
  const saveLatestRubric = (rubricId, rubricName, selectedGrade) => {
    const rubricToSave = {
      id: rubricId,
      name: rubricName,
      grade: selectedGrade,
      timestamp: Date.now(),
    };
    localStorage.setItem("latestSelectedRubric2", JSON.stringify(rubricToSave));
    setLatestRubric(rubricToSave);
  };

  // Add this function to handle opening the popover
  const handleRubricClick = (event, rubric) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserRubric(rubric);
  };

  const handleGradeSelect = (selectedGrade) => {
    setGrade(selectedGrade);
    onRubricSelect(selectedUserRubric.id);
    saveLatestRubric(
      selectedUserRubric.id,
      selectedUserRubric.name,
      selectedGrade,
    );
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchRubrics = async () => {
    setIsLoadingRubrics(true);
    try {
      const rubricsCollection = collection(db, "rubrics");

      // Fetch user-specific rubrics
      const userRubricsQuery = query(
        rubricsCollection,
        or(where("userId", "==", user.uid), where("sharedTo", "==", user.uid)),
        orderBy("createdAt", "desc"),
      );
      const userRubricsSnapshot = await getDocs(userRubricsQuery);
      const userRubricsList = userRubricsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserRubrics(userRubricsList);

      // Fetch standard rubrics
      const standardRubricsQuery = query(
        rubricsCollection,
        where("custom", "==", true),
        where("userId", "==", "Z6BLSMsm38Un5mgW0O6Z3TToHq12"),
      );
      const standardRubricsSnapshot = await getDocs(standardRubricsQuery);
      const standardRubricsList = standardRubricsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setStandardRubrics(standardRubricsList);

      // Set first rubric as selected if none is selected
      if (
        !selectedRubric &&
        (userRubricsList.length > 0 || standardRubricsList.length > 0)
      ) {
        // onRubricSelect(userRubricsList[0]?.id || standardRubricsList[0]?.id);
      }
    } catch (error) {
      console.error("Error fetching rubrics:", error);
    } finally {
      setIsLoadingRubrics(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRubrics();
    }
  }, [open, user.uid]);

  const subtitles = {
    Förklarande: "Förklara varför något händer eller hur något fungerar",
    Beskrivande: "Ge en detaljerad beskrivning av ämnet eller fenomenet",
    Argumenterande: "Övertyga läsaren med argument och bevis",
    Berättande: "Berätta en historia eller återge en händelse",
    Instruerande: "Visa hur man gör något steg för steg",
    Informerande: "Presentera fakta och information om ett ämne",
  };

  const EmptyStateCard = ({
    title,
    description,
    actionText,
    onActionClick,
    icon,
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: "center",
        border: "2px dashed",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
      }}
    >
      <Box sx={{ mb: 2, fontSize: "3rem" }}>{icon}</Box>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, fontSize: "0.95rem" }}
      >
        {description}
      </Typography>
      <Button
        variant="purp"
        onClick={onActionClick}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          px: 3,
        }}
      >
        {actionText}
      </Button>
    </Paper>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "650px" },
            maxWidth: "100%",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              pb: 0,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: theme.palette.mode === "dark" ? "" : "grey.50",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Välj bedömningsmatris
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.94rem" }}
                >
                  Välj en befintlig matris eller skapa en ny anpassad för
                  uppgiften
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="large">
                <CloseIcon />
              </IconButton>
            </Box>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: "48px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  minHeight: "48px",
                  px: 2,
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Tab
                icon={<TableChartIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Mina matriser
                    <Chip
                      label={userRubrics.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        bgcolor:
                          userRubrics.length === 0
                            ? "secondary.main"
                            : "primary.main",
                        color: "white",
                      }}
                    />
                  </Box>
                }
              />
              <Tab
                icon={<CorporateFareIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Standardmatriser
                    {standardRubrics.length > 0 && (
                      <Chip
                        label={6}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.75rem",
                          bgcolor:
                            userRubrics.length === 0
                              ? "primary.main"
                              : "secondary.main",
                          color: "white",
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {latestRubric && (
            <Box
              sx={{
                position: "relative",

                background: `linear-gradient(45deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  bgcolor: "primary.main",
                },
              }}
            >
              <Box sx={{ pl: 4, pr: 2, py: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "primary.main",
                        borderRadius: "50%",
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        fontSize: "0.95rem",
                      }}
                    >
                      Ditt senaste val
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem("latestSelectedRubric");
                      setLatestRubric(null);
                    }}
                    sx={{
                      opacity: 0.7,
                      "&:hover": {
                        opacity: 1,
                        bgcolor: "rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
                <Tooltip
                  title={latestRubric.name}
                  placement="top"
                  disableHoverListener={latestRubric.name.length <= 21}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2.5,
                      bgcolor: "background.paper",
                      borderRadius: 2,
                      cursor: "pointer",
                      border: "2px solid transparent",

                      transition: "all 0.25s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        transform: "scale(1.02)",
                        boxShadow: `0 4px 20px ${theme.palette.primary.main}25`,
                      },
                    }}
                    onClick={() => {
                      onRubricSelect(latestRubric.id);
                      setGrade(latestRubric.grade);
                      onClose();
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        border: "3px solid",
                        borderColor:
                          theme.palette.mode === "dark"
                            ? "grey.800"
                            : "grey.100",
                      }}
                    >
                      <TableChartIcon sx={{ fontSize: "1.3rem" }} />
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          mb: 0,
                          color:
                            theme.palette.mode === "dark"
                              ? "grey.100"
                              : "grey.800",
                        }}
                      >
                        {latestRubric.name.length > (isMobile ? 21 : 37)
                          ? `${latestRubric.name.substring(0, isMobile ? 21 : 37)}...`
                          : latestRubric.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 500,
                          fontSize: "0.85rem",
                        }}
                      >
                        {beautifyGradeText(latestRubric.grade)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Välj →
                    </Box>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Content */}
          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLoadingRubrics ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
                {activeTab === 0 ? (
                  userRubrics.length > 0 ? (
                    <>
                      <Alert
                        icon={<InfoIcon />}
                        severity="info"
                        sx={{ mb: 3, borderRadius: 2 }}
                      >
                        <Typography variant="body2">
                          Här är dina personliga matriser. Klicka för att välja
                          årskurs och använd.
                        </Typography>
                      </Alert>
                      <Grid container spacing={2}>
                        {userRubrics.map((rubric) => (
                          <Grid item xs={12} md={6} key={rubric.id}>
                            <Box
                              sx={{
                                position: "relative",
                                "&:hover .visibility-icon": {
                                  opacity: 1,
                                },
                              }}
                            >
                              <Tooltip
                                title={rubric.name}
                                placement="top"
                                disableHoverListener={rubric.name.length <= 21}
                              >
                                <div>
                                  <ThemeFlowBox
                                    icon={RadioButtonUncheckedIcon}
                                    title={rubric.name}
                                    isSelected={selectedRubric === rubric.id}
                                    onClick={(event) =>
                                      handleRubricClick(event, rubric)
                                    }
                                    diffView={false}
                                  />
                                </div>
                              </Tooltip>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : (
                    <EmptyStateCard
                      icon="📝"
                      title="Du har inga egna matriser än"
                      description="Skapa din första bedömningsmatris med hjälp av AI eller skapa manuellt."
                      actionText="Skapa ny matris"
                      onActionClick={() => navigate("/app/matris/ny")}
                    />
                  )
                ) : (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Filtrera på årskurs
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "none" }}
                      >
                        Välj årskurs för att se relevanta standardmatriser
                      </Typography>

                      <GradeSelector
                        grade={grade}
                        setGrade={setGrade}
                        hideSome={true}
                        //whiteVariant={true}
                      />
                    </Box>

                    {grade ? (
                      <>
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Matriser för {beautifyGradeText(grade)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.95rem" }}
                          >
                            Välj en standardmatris som passar ditt syfte, eller
                            skapa en ny.
                          </Typography>
                        </Box>

                        <Grid container spacing={2}>
                          {standardRubrics
                            .filter((rubric) => rubric.grade === grade)
                            .map((rubric) => (
                              <Grid item xs={12} md={6} key={rubric.id}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    "&:hover .visibility-icon": {
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <ThemeFlowBox
                                    icon={RadioButtonUncheckedIcon}
                                    title={rubric.name}
                                    subtitle={subtitles[rubric.name] || ""}
                                    isSelected={selectedRubric === rubric.id}
                                    onClick={() => {
                                      onRubricSelect(rubric.id);
                                      setGrade(rubric.grade);
                                      saveLatestRubric(
                                        rubric.id,
                                        rubric.name,
                                        rubric.grade,
                                      );
                                      onClose();
                                    }}
                                    diffView={false}
                                  />
                                </Box>
                              </Grid>
                            ))}
                        </Grid>

                        {standardRubrics.filter(
                          (rubric) => rubric.grade === grade,
                        ).length === 0 && (
                          <EmptyStateCard
                            icon="🔍"
                            title="Inga matriser för denna årskurs"
                            description="Det finns inga standardmatriser för vald årskurs. Prova en annan årskurs eller skapa en egen matris."
                            actionText="Skapa egen matris"
                            onActionClick={() => navigate("/app/matris/ny")}
                          />
                        )}
                      </>
                    ) : (
                      <EmptyStateCard
                        icon="🎓"
                        title="Välj årskurs"
                        description="Välj en årskurs ovan för att se tillgängliga standardmatriser för den nivån."
                        actionText="Skapa egen matris istället"
                        onActionClick={() => navigate("/app/matris/ny")}
                      />
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 3,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: theme.palette.mode === "dark" ? "" : "grey.50",
            }}
          >
            <Stack spacing={2}>
              <Button
                variant="success"
                fullWidth
                size="large"
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 3,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    animation: "shimmer 2s ease-in-out infinite",
                  },
                  "@keyframes shimmer": {
                    "0%": { left: "-100%" },
                    "50%": { left: "100%" },
                    "100%": { left: "100%" },
                  },
                }}
                startIcon={
                  <AutoFixHighIcon
                    sx={{
                      animation: "gentleRotate 3s linear infinite",
                      "@keyframes gentleRotate": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                }
                onClick={() => navigate("/app/matris/ny")}
              >
                Skapa ny matris
              </Button>
            </Stack>
          </Box>
        </Box>

        <GradeSelectorPopover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          onGradeSelect={handleGradeSelect}
          currentGrade={grade}
          rubricName={selectedUserRubric?.name || ""}
          setGrade={setGrade}
        />
      </Drawer>

      <RubricsDialog
        open={isRubricsDialogOpen}
        onClose={() => setIsRubricsDialogOpen(false)}
        user={user}
        onRubricsUpdated={fetchRubrics}
      />

      <RubricsDialog
        open={Boolean(viewingRubric)}
        onClose={() => setViewingRubric(null)}
        user={user}
        onRubricsUpdated={fetchRubrics}
        initialData={viewingRubric}
        watchOnly={true}
      />
    </>
  );
};

export default RubricsDrawer;
